import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { homeFor } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(homeFor(user), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden flex-col overflow-hidden bg-[#022a51] p-10 text-white lg:flex">
        <img src="/mokora-white.png" alt="Mokora" className="relative z-10 h-10 w-auto self-start" />
        <div className="relative z-10 flex flex-1 flex-col justify-center">
          <h1 className="font-heading text-5xl font-extrabold leading-none tracking-tight">
            Timesheet Tool
          </h1>
          <div className="mt-6 h-1.5 w-16 rounded-full bg-[hsl(var(--accent))]" />
        </div>
        <div className="relative z-10 font-mono text-xs text-slate-400/70">© 2026 Mokora</div>
        <img src="/mokora-icon.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 opacity-[0.06]" />
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 lg:hidden">
            <img src="/mokora-navy.png" alt="Mokora" className="h-9 w-auto dark:hidden" />
            <img src="/mokora-white.png" alt="Mokora" className="hidden h-9 w-auto dark:block" />
          </div>

          <h2 className="font-heading text-2xl font-extrabold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Enter your details to access your timesheets.</p>

          {error && (
            <div data-testid="auth-error" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          <form data-testid="auth-login-form" onSubmit={doLogin} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="l-email">Email</Label>
              <Input id="l-email" data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="l-password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-[#022a51] hover:underline dark:text-sky-300">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput id="l-password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-xs text-muted-foreground">
            Need an account? Contact your manager - accounts are created for you.
          </p>
        </div>
      </div>
    </div>
  );
}
